import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { Wave, FavoriteFrequency } from '../types';
import { SimulationConfig, Emitter, Point, WavePattern } from '../components/WavePool/types';
import { DEFAULT_CONFIG } from '../components/WavePool/constants';
import { presetLoader } from './presetLoader';

// Firebase Functions
const saveWaveConfig = httpsCallable(functions, 'saveWaveConfig');
const loadWaveConfig = httpsCallable(functions, 'loadWaveConfig');
const deleteWaveConfig = httpsCallable(functions, 'deleteWaveConfig');
const listUserConfigs = httpsCallable(functions, 'listUserConfigs');
const shareWaveConfig = httpsCallable(functions, 'shareWaveConfig');
const unshareWaveConfig = httpsCallable(functions, 'unshareWaveConfig');
const loadSharedConfig = httpsCallable(functions, 'loadSharedConfig');

// Wave Pool Firebase Functions
const saveWavePoolConfigFn = httpsCallable(functions, 'saveWavePoolConfig');
const loadWavePoolConfigFn = httpsCallable(functions, 'loadWavePoolConfig');
const listWavePoolConfigsFn = httpsCallable(functions, 'listWavePoolConfigs');
const deleteWavePoolConfigFn = httpsCallable(functions, 'deleteWavePoolConfig');
const updateWavePoolConfigFn = httpsCallable(functions, 'updateWavePoolConfig');

export interface WaveConfig {
  id?: string;
  name: string;
  description?: string;
  waves: Wave[];
  speed?: number;
  tags?: string[];
  isPublic?: boolean;
  userId?: string;
  createdAt?: any;
  updatedAt?: any;
  lastAccessedAt?: any;
  version?: string;
  source?: 'personal' | 'shared';
  category?: string;
}

export interface ConfigListOptions {
  limit?: number;
  offset?: number;
  includeShared?: boolean;
  tags?: string[];
}

export interface WavePoolPreset {
  id: string;
  name: string;
  description?: string;
  poolConfig: SimulationConfig;
  emitters: Emitter[];
  focusPoints?: Point[];
  tags?: string[];
  category?: string;
}

class ConfigService {
  // Save wave configuration
  async saveConfig(config: {
    name: string;
    description?: string;
    waves: Wave[];
    speed?: number;
    tags?: string[];
    isPublic?: boolean;
  }): Promise<string> {
    try {
      const result = await saveWaveConfig(config);
      const data = result.data as { success: boolean; error?: string; configId?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to save configuration');
      }
      
      return data.configId as string;
    } catch (error) {
      console.error('Save configuration error:', error);
      throw error;
    }
  }

  // Load wave configuration
  async loadConfig(configId: string, source: 'personal' | 'shared' = 'personal'): Promise<WaveConfig> {
    try {
      const result = await loadWaveConfig({ configId, source });
      const data = result.data as { success: boolean; error?: string; config?: any };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load configuration');
      }
      
      return data.config;
    } catch (error) {
      console.error('Load configuration error:', error);
      throw error;
    }
  }

  // Delete wave configuration
  async deleteConfig(configId: string, deleteFromShared: boolean = false): Promise<void> {
    try {
      const result = await deleteWaveConfig({ configId, deleteFromShared });
      const data = result.data as { success: boolean; error?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete configuration');
      }
    } catch (error) {
      console.error('Delete configuration error:', error);
      throw error;
    }
  }

  // List user configurations
  async listConfigs(options: ConfigListOptions = {}): Promise<{
    configs: WaveConfig[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      const result = await listUserConfigs(options);
      const data = result.data as { 
        success: boolean; 
        error?: string; 
        configs?: WaveConfig[];
        total?: number;
        hasMore?: boolean;
      };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to list configurations');
      }
      
      return {
        configs: data.configs || [],
        total: data.total || 0,
        hasMore: data.hasMore || false
      };
    } catch (error) {
      console.error('List configurations error:', error);
      throw error;
    }
  }

  // Share wave configuration
  async shareConfig(configId: string): Promise<string> {
    try {
      const result = await shareWaveConfig({ configId });
      const data = result.data as { success: boolean; error?: string; sharedId?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to share configuration');
      }
      
      return data.sharedId as string;
    } catch (error) {
      console.error('Share configuration error:', error);
      throw error;
    }
  }

  // Unshare wave configuration
  async unshareConfig(configId: string): Promise<void> {
    try {
      const result = await unshareWaveConfig({ configId });
      const data = result.data as { success: boolean; error?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to unshare configuration');
      }
    } catch (error) {
      console.error('Unshare configuration error:', error);
      throw error;
    }
  }

  // Load shared wave configuration (no authentication required)
  async loadSharedConfig(sharedId: string): Promise<WaveConfig> {
    try {
      const result = await loadSharedConfig({ sharedId });
      const data = result.data as { success: boolean; error?: string; config?: any };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load shared configuration');
      }
      
      return data.config;
    } catch (error) {
      console.error('Load shared configuration error:', error);
      throw error;
    }
  }

  // Create preset configurations
  async getPresetConfigurations(): Promise<WaveConfig[]> {
    return presetLoader.loadWavePresets();
  }

  // Get presets by category
  async getPresetConfigurationsByCategory(category: string): Promise<WaveConfig[]> {
    return presetLoader.getWavePresetsByCategory(category);
  }

  // Search presets
  async searchPresets(query: string): Promise<WaveConfig[]> {
    return presetLoader.searchWavePresets(query);
  }

  // Get available preset categories
  async getPresetCategories(): Promise<string[]> {
    return presetLoader.getWavePresetCategories();
  }

  // Quick save current configuration
  async quickSave(waves: Wave[], name?: string): Promise<string> {
    const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const configName = name || `Quick Save ${timestamp}`;
    
    return this.saveConfig({
      name: configName,
      description: 'Auto-saved configuration',
      waves,
      tags: ['auto-save'],
      isPublic: false
    });
  }

  // Export configuration as JSON
  exportConfig(config: WaveConfig): string {
    const exportData = {
      name: config.name,
      description: config.description,
      waves: config.waves,
      tags: config.tags,
      version: '1.0',
      exportedAt: new Date().toISOString(),
      source: 'Resynth Lab'
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  // Import configuration from JSON
  importConfig(jsonData: string): WaveConfig {
    try {
      const data = JSON.parse(jsonData);
      
      if (!data.name || !Array.isArray(data.waves)) {
        throw new Error('Invalid configuration format');
      }
      
      return {
        name: data.name,
        description: data.description || '',
        waves: data.waves,
        tags: data.tags || [],
        isPublic: false
      };
    } catch (error) {
      console.error('Import configuration error:', error);
      throw new Error('Failed to import configuration');
    }
  }

  // Favorite Frequencies Management
  private readonly FAVORITES_STORAGE_KEY = 'resynthLab_favorites';

  // Save a favorite frequency
  saveFavoriteFrequency(name: string, wave: Partial<Wave>): string {
    const favorites = this.listFavoriteFrequencies();
    const newFavorite: FavoriteFrequency = {
      id: `fav_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name.trim(),
      freq: wave.freq || 440,
      amp: wave.amp || 50,
      phase: wave.phase || 0,
      color: wave.color || '#3B82F6',
      channel: wave.channel || 'both',
      autoPanRate: wave.autoPanRate,
      autoPanDepth: wave.autoPanDepth,
      waveform: wave.waveform || 'sine',
      createdAt: Date.now()
    };
    
    favorites.push(newFavorite);
    localStorage.setItem(this.FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    return newFavorite.id;
  }

  // List all favorite frequencies
  listFavoriteFrequencies(): FavoriteFrequency[] {
    try {
      const data = localStorage.getItem(this.FAVORITES_STORAGE_KEY);
      if (!data) return [];
      const favorites = JSON.parse(data);
      // Sort by most recent first
      return favorites.sort((a: FavoriteFrequency, b: FavoriteFrequency) => b.createdAt - a.createdAt);
    } catch (error) {
      console.error('Error loading favorites:', error);
      return [];
    }
  }

  // Delete a favorite frequency
  deleteFavoriteFrequency(id: string): void {
    const favorites = this.listFavoriteFrequencies();
    const filtered = favorites.filter(fav => fav.id !== id);
    localStorage.setItem(this.FAVORITES_STORAGE_KEY, JSON.stringify(filtered));
  }

  // Get a single favorite by ID
  getFavoriteFrequency(id: string): FavoriteFrequency | null {
    const favorites = this.listFavoriteFrequencies();
    return favorites.find(fav => fav.id === id) || null;
  }

  // Convert favorite to Wave (for adding to workspace)
  favoriteToWave(favorite: FavoriteFrequency, waveId: number): Wave {
    return {
      id: waveId,
      freq: favorite.freq,
      amp: favorite.amp,
      phase: favorite.phase,
      color: favorite.color,
      muted: false,
      channel: favorite.channel,
      autoPanRate: favorite.autoPanRate,
      autoPanDepth: favorite.autoPanDepth,
      waveform: favorite.waveform || 'sine'
    };
  }

  // Wave Pool presets
  async getWavePoolPresetConfigurations(): Promise<WavePoolPreset[]> {
    return presetLoader.loadWavePoolPresets();
  }

  // Get WavePool presets by category
  async getWavePoolPresetsByCategory(category: string): Promise<WavePoolPreset[]> {
    return presetLoader.getWavePoolPresetsByCategory(category);
  }

  // Search WavePool presets
  async searchWavePoolPresets(query: string): Promise<WavePoolPreset[]> {
    return presetLoader.searchWavePoolPresets(query);
  }

  // Get available WavePool preset categories
  async getWavePoolPresetCategories(): Promise<string[]> {
    return presetLoader.getWavePoolPresetCategories();
  }
  // Wave Pool Configuration storage (database-based)
  
  // Save Wave Pool configuration to database
  async saveWavePoolConfig(config: {
    name: string;
    description?: string;
    poolConfig: any; // SimulationConfig
    emitters: any[]; // Emitter[]
    focusPoints?: any[]; // Point[]
    tags?: string[];
    isPublic?: boolean;
  }): Promise<string> {
    try {
      const result = await saveWavePoolConfigFn(config);
      const data = result.data as { success: boolean; error?: string; configId?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to save wave pool configuration');
      }
      
      return data.configId as string;
    } catch (error) {
      console.error('Save wave pool configuration error:', error);
      throw error;
    }
  }

  // Load Wave Pool configuration from database
  async loadWavePoolConfig(configId: string, source: 'personal' | 'shared' = 'personal'): Promise<any> {
    try {
      const result = await loadWavePoolConfigFn({ configId, source });
      const data = result.data as { success: boolean; error?: string; config?: any };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load wave pool configuration');
      }
      
      return data.config;
    } catch (error) {
      console.error('Load wave pool configuration error:', error);
      throw error;
    }
  }

  // List all Wave Pool configurations
  async listWavePoolConfigs(options?: {
    limit?: number;
    offset?: number;
    includeShared?: boolean;
    tags?: string[];
  }): Promise<any[]> {
    try {
      const result = await listWavePoolConfigsFn(options || {});
      const data = result.data as { success: boolean; error?: string; configs?: any[] };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to list wave pool configurations');
      }
      
      return data.configs || [];
    } catch (error) {
      console.error('List wave pool configurations error:', error);
      return [];
    }
  }

  // Delete Wave Pool configuration
  async deleteWavePoolConfig(configId: string, deleteFromShared: boolean = false): Promise<void> {
    try {
      const result = await deleteWavePoolConfigFn({ configId, deleteFromShared });
      const data = result.data as { success: boolean; error?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete wave pool configuration');
      }
    } catch (error) {
      console.error('Delete wave pool configuration error:', error);
      throw error;
    }
  }

  // Update Wave Pool configuration
  async updateWavePoolConfig(configId: string, updates: Partial<any>): Promise<void> {
    try {
      const result = await updateWavePoolConfigFn({ configId, updates });
      const data = result.data as { success: boolean; error?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to update wave pool configuration');
      }
    } catch (error) {
      console.error('Update wave pool configuration error:', error);
      throw error;
    }
  }
}

export default new ConfigService();